import { JobFormData } from '@/jobs/application/types/JobTypes';
import { useJobFormData } from '@/jobs/presentation/hooks/useJobFormData';
import { useMemo } from 'react';
import JobsDrawerForm from './JobsDrawerForm';
import DrawerLayout from '@/common/presentation/components/DrawerLayout/DrawerLayout';
import { useJobDateStore } from '@/store/JobDateStore';
import { format } from 'date-fns';
import { useFeatureFlag } from '@/common/presentation/hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '@/common/domain/constants/featureFlags';
import { RaptorJobCreateModal } from '@/raptorjob/presentation/components/RaptorJobCreatePage/RaptorJobCreateModal';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import tid from '@/common/infrastructure/config/tid';

interface DrawerProps {
  open: boolean;
  selectedJobId?: number;
  onClose: () => void;
  onStartDateChange?: (nextDate: Date | string) => void;
  onAfterSave?: (nextDate: Date | string) => void;
  initialValues?: JobFormData;
  initialCustomerId?: number;
  onJobCreated?: (id: string) => void;
}

export default function JobDrawer({
  open,
  onClose,
  selectedJobId,
  initialValues = {},
  onStartDateChange,
  onAfterSave,
  initialCustomerId,
  onJobCreated
}: Readonly<DrawerProps>) {
  const { isNew, job, jobLoading, formOptionsLoading } = useJobFormData(selectedJobId, !open);
  const { selectedDate: storeSelectedDate } = useJobDateStore();
  const navigate = useNavigate();
  const useNewModal = useFeatureFlag(FEATURE_FLAGS.JOBS_ERP_CREATE_MODAL_NEW_LOOK_AND_FEEL);
  const isERPJobDetailsEnabled = useFeatureFlag(FEATURE_FLAGS.JOBS_ERP_DETAILS_PAGE_NEW_LOOK_AND_FEEL);

  const formikStatus = useMemo(() => ({ loading: jobLoading || formOptionsLoading }), [jobLoading, formOptionsLoading]);

  const initialValuesWithStoreDate = useMemo(() => {
    if (isNew && storeSelectedDate) {
      const startDateString = format(storeSelectedDate, 'yyyy-MM-dd');
      return { ...job, ...initialValues, startDate: startDateString };
    }
    return isNew ? { ...job, ...initialValues } : job;
  }, [isNew, job, initialValues, storeSelectedDate]);

  const title = isNew ? 'Create New Job' : 'Job Details';

  // Handle job creation - use custom handler if provided, otherwise navigate to job details page
  const handleOpenCreatedJob = (id: string) => {
    if (onJobCreated) {
      onJobCreated(id);
    } else {
      onClose();
      const detailsQueryString = isERPJobDetailsEnabled
        ? '?module=jobs&submodule=dispatch'
        : '?tab=information&module=jobs&submodule=dispatch';

      navigate(`/jobs/${id}${detailsQueryString}`);
    }
  };

  // If feature flag is enabled and creating a new job, render RaptorJobCreateModal (it renders its own Dialog)
  if (useNewModal && open && isNew) {
    return (
      <Box sx={{ display: 'contents' }} {...tid('jobs:dispatch:job-drawer')}>
        <RaptorJobCreateModal
          handleClose={onClose}
          openCreatedJob={handleOpenCreatedJob}
          isERPJob={true}
          mode="create"
          initialCustomerId={initialCustomerId}
        />
      </Box>
    );
  }

  // Default: render JobsDrawerForm in DrawerLayout
  return (
    <Box sx={{ display: 'contents' }} {...tid('jobs:dispatch:job-drawer')}>
      <DrawerLayout open={open} onClose={onClose} title={title} width="450px" keepMounted={false} testId="jobs:dispatch:job-drawer">
        <JobsDrawerForm
          isOpen={open}
          jobId={selectedJobId || 0}
          loading={jobLoading}
          selectedJobId={selectedJobId || 0}
          isNew={isNew}
          initialValues={initialValuesWithStoreDate}
          formikStatus={formikStatus}
          onClose={onClose}
          onStartDateChange={onStartDateChange}
          onAfterSave={onAfterSave}
        />
      </DrawerLayout>
    </Box>
  );
}
